import React, { useEffect, useState } from "react";
import {
  Mail,
  MessageSquare,
  Loader2,
  Inbox as InboxIcon,
  CheckCircle2,
  Reply,
  Building2,
  CalendarClock,
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Phase 2 — applicant inbox.
 *
 * Lists recruiter-initiated messages newest first. Supports:
 *   - Mark as read on first expand
 *   - One-click reply (mailto:) using the recruiter's reply_to address
 *
 * Props:
 *   - onCountChange(unreadCount): bubbles unread count up so the sidebar
 *     badge stays in sync.
 */
export default function InboxSection({ onCountChange }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const reload = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const token = localStorage.getItem("access_token");
      const res = await fetch(`${API}/api/inbox`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Could not load inbox");
      const data = await res.json();
      setMessages(data || []);
      const unread = (data || []).filter((m) => m.status === "new").length;
      onCountChange?.(unread);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, [onCountChange]);

  useEffect(() => {
    reload();
  }, [reload]);

  async function markRead(id) {
    try {
      const token = localStorage.getItem("access_token");
      await fetch(`${API}/api/inbox/${id}/read`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages((prev) =>
        prev.map((m) => (m.id === id ? { ...m, status: "read" } : m))
      );
      const newUnread = messages.filter(
        (m) => m.id !== id && m.status === "new"
      ).length;
      onCountChange?.(newUnread);
    } catch {
      toast.error("Could not mark as read");
    }
  }

  function toggle(m) {
    const next = expandedId === m.id ? null : m.id;
    setExpandedId(next);
    if (next && m.status === "new") markRead(m.id);
  }

  function buildReplyMailto(m) {
    const subject = encodeURIComponent(
      m.type === "interview_request"
        ? `Re: Interview request from ${m.recruiter_company}`
        : `Re: Follow-up questions from ${m.recruiter_company}`
    );
    const intro =
      m.type === "interview_request"
        ? `Hi ${m.recruiter_name},\n\nThanks for the note — happy to chat. `
        : `Hi ${m.recruiter_name},\n\nThanks for the questions. Here are quick answers:\n\n`;
    const body = encodeURIComponent(intro);
    return `mailto:${m.recruiter_email}?subject=${subject}&body=${body}`;
  }

  return (
    <div className="space-y-4" data-testid="inbox-section">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <InboxIcon className="h-6 w-6 text-blue-600" />
            Inbox
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Recruiter messages routed through Inter-View. Reply directly from
            here — your personal email stays private.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={reload}
          disabled={loading}
          data-testid="inbox-refresh-btn"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Refresh"}
        </Button>
      </div>

      {loading && (
        <div className="text-center py-12 text-slate-500" data-testid="inbox-loading">
          <Loader2 className="h-5 w-5 animate-spin mx-auto" />
        </div>
      )}

      {!loading && error && (
        <div className="rounded-xl bg-rose-50 border border-rose-200 p-4 text-sm text-rose-700" data-testid="inbox-error">
          {error}
        </div>
      )}

      {!loading && !error && messages.length === 0 && (
        <div
          className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center"
          data-testid="inbox-empty"
        >
          <InboxIcon className="h-10 w-10 text-slate-300 mx-auto" />
          <p className="mt-3 text-sm font-semibold text-slate-700">
            No messages yet
          </p>
          <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
            Once recruiters scan your QR code and reach out, their messages will
            land here. You&rsquo;ll also be notified by email.
          </p>
        </div>
      )}

      {!loading && !error && messages.length > 0 && (
        <ul className="space-y-3" data-testid="inbox-messages">
          {messages.map((m) => {
            const isOpen = expandedId === m.id;
            const isUnread = m.status === "new";
            const isInterview = m.type === "interview_request";
            return (
              <li
                key={m.id}
                className={`rounded-xl border overflow-hidden transition-shadow ${
                  isUnread
                    ? "border-blue-200 bg-blue-50/40 shadow-sm"
                    : "border-slate-200 bg-white"
                }`}
                data-testid={`inbox-message-${m.id}`}
              >
                <button
                  type="button"
                  onClick={() => toggle(m)}
                  className="w-full text-left px-4 py-3 sm:px-5 sm:py-4 flex items-start gap-3 hover:bg-slate-50/60"
                >
                  <div className="mt-0.5 flex-shrink-0">
                    {isInterview ? (
                      <Mail className="h-5 w-5 text-blue-600" />
                    ) : (
                      <MessageSquare className="h-5 w-5 text-emerald-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-900 text-sm">
                        {m.recruiter_name}
                      </span>
                      <span className="text-slate-400 text-xs">·</span>
                      <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                        <Building2 className="h-3 w-3" />
                        {m.recruiter_company}
                      </span>
                      {isUnread && (
                        <Badge className="bg-blue-600 hover:bg-blue-600 text-white text-[10px] px-1.5 py-0">
                          NEW
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
                      <CalendarClock className="h-3 w-3" />
                      {new Date(m.created_at).toLocaleString()}
                      <span className="text-slate-300 mx-1">·</span>
                      {isInterview ? "Interview request" : "Follow-up questions"}
                    </p>
                    {!isOpen && (
                      <p className="mt-1 text-sm text-slate-700 line-clamp-2">
                        {isInterview
                          ? m.message
                          : (m.questions || []).map((q, i) => `${i + 1}. ${q}`).join(" ")}
                      </p>
                    )}
                  </div>
                  {!isUnread && (
                    <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                  )}
                </button>
                {isOpen && (
                  <div className="border-t border-slate-200 px-4 py-3 sm:px-5 sm:py-4 bg-white">
                    {isInterview && m.message && (
                      <p
                        className="text-sm text-slate-800 whitespace-pre-wrap"
                        data-testid={`inbox-message-${m.id}-message`}
                      >
                        {m.message}
                      </p>
                    )}
                    {!isInterview && (m.questions || []).length > 0 && (
                      <ol
                        className="text-sm text-slate-800 list-decimal list-inside space-y-1"
                        data-testid={`inbox-message-${m.id}-questions`}
                      >
                        {m.questions.map((q, i) => (
                          <li key={i}>{q}</li>
                        ))}
                      </ol>
                    )}
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Button
                        asChild
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        data-testid={`inbox-message-${m.id}-reply-btn`}
                      >
                        <a href={buildReplyMailto(m)}>
                          <Reply className="h-3.5 w-3.5 mr-1.5" />
                          Reply to {m.recruiter_email}
                        </a>
                      </Button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
